import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { isGroupMode } from 'lib/merchant-group'
import { parseCookies } from 'nookies'

import type { MobileCardsConfig } from 'components/Table/MobileCardList'
import {
  DEFAULT_TABLE_DISPLAY,
  type TableDensity,
  type TableDisplayPref,
  type TableFontSize
} from 'components/Table/tableDisplay'

import { saveTablePreference, deleteTablePreference } from './api'
import { readPref, writePref, clearPref } from './storage'
import type { ColumnDef, ColumnId, TableColumnPref } from './types'
import { resolveColumns } from './utils'

// TODO(tech-debt): preferir expor `merchantUserUuid` no AuthContext (single source)
// e consumir via `useAuthContext()`. Hoje lê direto do cookie por simplicidade.
function readMerchantUserUuid(): string {
  if (typeof document === 'undefined') return ''
  const cookies = parseCookies()
  return cookies['nextauth.merchant_user_uuid'] || ''
}

const EMPTY_PREF: TableColumnPref = { visibility: {}, order: [] }

interface UseTableColumnsOpts<TRow> {
  tableId: string
  catalog: ColumnDef<TRow>[]
}

/** Display + sticky props ready to spread onto <EnhancedTable> / <Table>. */
interface TableProps {
  fontSize: TableFontSize
  density: TableDensity
  stickyEndColumns: ColumnId[]
  mobileCards: MobileCardsConfig
}

interface UseTableColumnsReturn<TRow> {
  visibleColumns: ColumnDef<TRow>[]
  allColumns: ColumnDef<TRow>[]
  pref: TableColumnPref
  display: TableDisplayPref
  setVisibility: (id: ColumnId, visible: boolean) => void
  reorder: (orderedUnlockedIds: ColumnId[]) => void
  setDisplay: (next: Partial<TableDisplayPref>) => void
  applyPref: (next: TableColumnPref) => void
  reset: () => void
  isReady: boolean
  /** Spread onto the table component: font size, density and sticky columns. */
  tableProps: TableProps
}

export function useTableColumns<TRow>(
  opts: UseTableColumnsOpts<TRow>
): UseTableColumnsReturn<TRow> {
  const { tableId: baseTableId, catalog: rawCatalog } = opts
  // SC-10999: na visão multi-empresas a preferência fica numa chave própria.
  // O usuário é o da empresa de contexto; com a mesma chave, mexer nas colunas
  // no grupo (que tem a coluna Empresa) alterava a visão normal dessa empresa.
  const tableId = isGroupMode() ? `${baseTableId}@group` : baseTableId
  // SC-10999: colunas `groupOnly` só na visão multi-empresas. O cookie do grupo
  // só muda com hard reload (troca de conta), então ler no render é estável.
  const groupMode = isGroupMode()
  const catalog = useMemo(
    () =>
      rawCatalog.filter((col) =>
        groupMode ? !col.groupHidden : !col.groupOnly
      ),
    [rawCatalog, groupMode]
  )
  const [merchantUserId, setMerchantUserId] = useState<string>('')
  const [pref, setPref] = useState<TableColumnPref>(EMPTY_PREF)
  const [isReady, setIsReady] = useState(false)

  // Hydrate from cookie + localStorage on client mount, and re-hydrate when
  // the merchant_user_uuid cookie changes (login/logout into different merchant).
  useEffect(() => {
    const id = readMerchantUserUuid()
    setMerchantUserId(id)
    const stored = id ? readPref(id, tableId) : null
    setPref(stored ?? EMPTY_PREF)
    setIsReady(true)
  }, [tableId])

  // Debounced persistence
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const persist = useCallback(
    (next: TableColumnPref) => {
      if (!merchantUserId) return
      if (debounceRef.current) clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        writePref(merchantUserId, tableId, next)
        // Write-through: backend e a fonte da verdade (fire-and-forget).
        saveTablePreference(tableId, next)
      }, 200)
    },
    [merchantUserId, tableId]
  )

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [])

  const unlockedIds = useMemo(
    () => new Set(catalog.filter((c) => !c.locked).map((c) => c.id)),
    [catalog]
  )

  const setVisibility = useCallback(
    (id: ColumnId, visible: boolean) => {
      setPref((prev) => {
        const next: TableColumnPref = {
          ...prev,
          visibility: { ...prev.visibility, [id]: visible }
        }
        persist(next)
        return next
      })
    },
    [persist]
  )

  const reorder = useCallback(
    (orderedUnlockedIds: ColumnId[]) => {
      const filtered = orderedUnlockedIds.filter((id) => unlockedIds.has(id))
      setPref((prev) => {
        const next: TableColumnPref = { ...prev, order: filtered }
        persist(next)
        return next
      })
    },
    [persist, unlockedIds]
  )

  const setDisplay = useCallback(
    (next: Partial<TableDisplayPref>) => {
      setPref((prev) => {
        const updated: TableColumnPref = {
          ...prev,
          display: {
            ...DEFAULT_TABLE_DISPLAY,
            ...prev.display,
            ...next
          }
        }
        persist(updated)
        return updated
      })
    },
    [persist]
  )

  const applyPref = useCallback(
    (next: TableColumnPref) => {
      const filteredOrder = next.order.filter((id) => unlockedIds.has(id))
      const normalized: TableColumnPref = {
        visibility: { ...next.visibility },
        order: filteredOrder,
        ...(next.display && { display: next.display })
      }
      setPref(normalized)
      persist(normalized)
    },
    [persist, unlockedIds]
  )

  const reset = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)
    if (merchantUserId) {
      clearPref(merchantUserId, tableId)
      // Write-through: remove a entrada no backend (delete-on-null).
      deleteTablePreference(tableId)
    }
    setPref(EMPTY_PREF)
  }, [merchantUserId, tableId])

  // Resolution only depends on order + visibility, never on display. Keying the
  // memo to a content signature keeps `visibleColumns` referentially stable when
  // only font/density change — otherwise consumers that refetch on
  // `visibleColumns` identity would reload the table on every Save.
  const columnsSig = useMemo(
    () => JSON.stringify({ order: pref.order, visibility: pref.visibility }),
    [pref.order, pref.visibility]
  )
  const { visibleColumns, allColumns } = useMemo(
    () => resolveColumns(catalog, pref),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalog, columnsSig]
  )

  const display = useMemo<TableDisplayPref>(
    () => ({ ...DEFAULT_TABLE_DISPLAY, ...pref.display }),
    [pref.display]
  )

  const stickyEndColumns = useMemo(
    () =>
      catalog
        .filter((c) => c.locked && c.lockedPosition === 'end')
        .map((c) => c.id),
    [catalog]
  )

  // Versão em cards no mobile. O catálogo já diz tudo que o card precisa: o
  // rótulo em texto puro (o `header()` pode ser um ReactNode), a coluna travada
  // no início vira o título e a travada no fim vira o rodapé de ações. O badge
  // é inferido pelo id/rótulo de status.
  const mobileCards = useMemo<MobileCardsConfig>(
    () => ({
      columns: visibleColumns.map((col) => ({ id: col.id, label: col.label })),
      titleColumn: visibleColumns.find(
        (col) => col.locked && col.lockedPosition === 'start'
      )?.id,
      actionsColumn: visibleColumns.find(
        (col) => col.locked && col.lockedPosition === 'end'
      )?.id
    }),
    [visibleColumns]
  )

  const tableProps = useMemo<TableProps>(
    () => ({
      fontSize: display.fontSize,
      density: display.density,
      stickyEndColumns,
      mobileCards
    }),
    [display.fontSize, display.density, stickyEndColumns, mobileCards]
  )

  return {
    visibleColumns,
    allColumns,
    pref,
    display,
    setVisibility,
    reorder,
    setDisplay,
    applyPref,
    reset,
    isReady,
    tableProps
  }
}
